import React, { useState } from 'react';
import Icon from '../AppIcon';
import { supabase } from '../../lib/supabase';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from '../../contexts/AuthContext';

const stripePromise = loadStripe(import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY);

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    pricePerUser: 19,
    maxUsers: 5,
    features: ['Up to 5 users', 'Ticket & job management', 'Mobile technician app', 'Basic reporting', 'Email support']
  },
  {
    id: 'professional',
    name: 'Professional',
    pricePerUser: 29,
    maxUsers: 25,
    badge: 'Most Popular',
    features: ['Up to 25 users', 'Everything in Starter', 'GPS tracking', 'Invoice & quote management', 'Customer portal', 'Priority support']
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    pricePerUser: 49,
    maxUsers: 999,
    badge: 'Unlimited',
    features: ['Unlimited users', 'Everything in Professional', 'Custom integrations', 'Advanced analytics', 'Dedicated account manager', '24/7 support']
  }
];

const PaymentForm = ({ onSuccess, onError, planId, userCount, billingInfo }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError('');

    try {
      const { error: submitError } = await elements?.submit();
      if (submitError) {
        setError(submitError?.message || 'Please check your card details and try again.');
        setProcessing(false);
        return;
      }

      const { error: stripeError, paymentMethod } = await stripe?.createPaymentMethod({
        elements,
        params: { billing_details: { name: billingInfo?.billing_name, email: billingInfo?.billing_email } }
      });

      if (stripeError) {
        setError(stripeError?.message || 'Payment failed. Please try again.');
        setProcessing(false);
        return;
      }

      // Save payment method and activate subscription
      const { error: dbError } = await supabase?.from('org_subscriptions')?.update({
        status: 'active',
        plan: planId,
        max_users: userCount,
        payment_method_added: true,
        stripe_payment_method_id: paymentMethod?.id,
        account_suspended_at: null,
        suspension_reason: null,
        ...billingInfo,
        updated_at: new Date()?.toISOString()
      })?.not('id', 'is', null);

      if (dbError) throw dbError;

      // Log trial conversion event
      const { data: org } = await supabase?.from('organizations')?.select('id')?.limit(1)?.single();
      if (org?.id) {
        await supabase?.from('trial_events')?.insert([{
          organization_id: org?.id,
          event_type: 'trial_converted',
          event_data: { plan: planId, user_count: userCount }
        }]);
      }

      onSuccess?.();
    } catch (err) {
      setError(err?.message || 'Failed to activate subscription.');
      onError?.(err);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <Icon name="AlertCircle" size={14} color="#dc2626" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
      <button
        type="submit"
        disabled={!stripe || processing}
        className="w-full py-3 bg-primary text-primary-foreground rounded-lg font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {processing ? (
          <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Processing...</>
        ) : (
          <><Icon name="Lock" size={16} color="currentColor" /> Activate Subscription</>
        )}
      </button>
    </form>
  );
};

const TrialExpiredModal = ({ isOpen, onActivated }) => {
  const [step, setStep] = useState('plan');
  const [selectedPlan, setSelectedPlan] = useState('professional');
  const [userCount, setUserCount] = useState(5);
  const [billingForm, setBillingForm] = useState({ billing_name: '', billing_email: '', billing_address: '' });
  const [clientSecret, setClientSecret] = useState(null);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [activated, setActivated] = useState(false);
  const [activationDate, setActivationDate] = useState(null);
  const { signOut } = useAuth();

  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [showPromoField, setShowPromoField] = useState(false);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);

  if (!isOpen) return null;

  const selectedPlanData = PLANS?.find(p => p?.id === selectedPlan) || PLANS?.[1];
  const baseTotal = selectedPlanData?.pricePerUser * userCount;

  // Calculate discounted total
  const discountedTotal = (() => {
    if (!appliedPromo) return baseTotal;
    if (appliedPromo?.percentOff) return baseTotal * (1 - appliedPromo?.percentOff / 100);
    if (appliedPromo?.amountOff) return Math.max(0, baseTotal - appliedPromo?.amountOff / 100);
    return baseTotal;
  })();
  const monthlyTotal = discountedTotal?.toFixed(2);
  const baseMonthlyTotal = baseTotal?.toFixed(2);

  const handleValidatePromo = async () => {
    if (!promoCodeInput?.trim()) {
      setPromoError('Please enter a promo code.');
      return;
    }
    setPromoLoading(true);
    setPromoError('');
    try {
      const { data, error: fnError } = await supabase?.functions?.invoke('validate-promo-code', {
        body: { promoCode: promoCodeInput?.trim()?.toUpperCase() }
      });
      if (fnError) {
        // Extract actual error message from edge function response body
        let errMsg = fnError?.message || 'Failed to validate promo code. Please try again.';
        try {
          const body = await fnError?.context?.json?.();
          if (body?.error) errMsg = body?.error;
        } catch {}
        setPromoError(errMsg);
        return;
      }
      if (data?.error) {
        setPromoError(data?.error);
        return;
      }
      setAppliedPromo({
        code: promoCodeInput?.trim()?.toUpperCase(),
        promotionCodeId: data?.promotionCodeId,
        couponId: data?.couponId,
        percentOff: data?.percentOff || null,
        amountOff: data?.amountOff || null,
        name: data?.name || promoCodeInput?.trim()?.toUpperCase(),
        duration: data?.duration || 'once',
      });
      setPromoCode(promoCodeInput?.trim()?.toUpperCase());
      setPromoCodeInput('');
      setShowPromoField(false);
    } catch (err) {
      setPromoError(err?.message || 'Failed to validate promo code. Please try again.');
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoCode('');
    setPromoCodeInput('');
    setPromoError('');
    setShowPromoField(false);
  };

  const formatDiscount = (promo) => {
    if (promo?.percentOff) return `${promo?.percentOff}% off`;
    if (promo?.amountOff) return `$${(promo?.amountOff / 100)?.toFixed(2)} off`;
    return 'Discount applied';
  };

  const handleProceedToPayment = async () => {
    if (!billingForm?.billing_name || !billingForm?.billing_email) {
      setPaymentError('Please fill in billing name and email.');
      return;
    }
    setPaymentError('');
    setLoadingPayment(true);
    try {
      // If promo gives 100% off, skip payment and activate directly
      if (parseFloat(monthlyTotal) === 0) {
        const { error: dbError } = await supabase?.from('org_subscriptions')?.update({
          status: 'active',
          plan: selectedPlan,
          max_users: userCount,
          payment_method_added: false,
          account_suspended_at: null,
          suspension_reason: null,
          ...billingForm,
          updated_at: new Date()?.toISOString()
        })?.not('id', 'is', null);
        if (dbError) throw dbError;

        // Log trial conversion event
        const { data: org } = await supabase?.from('organizations')?.select('id')?.limit(1)?.single();
        if (org?.id) {
          await supabase?.from('trial_events')?.insert([{
            organization_id: org?.id,
            event_type: 'trial_converted',
            event_data: { plan: selectedPlan, user_count: userCount, promo_code: appliedPromo?.code, free_activation: true }
          }]);
        }

        handleActivated();
        return;
      }

      const { data, error } = await supabase?.functions?.invoke('create-payment-intent', {
        body: {
          paymentData: {
            amount: parseFloat(monthlyTotal),
            currency: 'usd',
            tableName: 'org_subscriptions',
            description: `SERVICEJOCKEY ${selectedPlanData?.name} Plan - ${userCount} users`,
            ...(appliedPromo?.promotionCodeId ? { promotionCodeId: appliedPromo?.promotionCodeId } : {}),
          },
          customerInfo: {
            userId: null,
            firstName: billingForm?.billing_name?.split(' ')?.[0] || billingForm?.billing_name,
            lastName: billingForm?.billing_name?.split(' ')?.slice(1)?.join(' ') || '',
            email: billingForm?.billing_email,
            billing: { address_line_1: billingForm?.billing_address, country: 'US' }
          }
        }
      });
      if (error) {
        // Extract actual error message from edge function response body
        let errMsg = error?.message || 'Failed to initialize payment. Please try again.';
        try {
          const body = await error?.context?.json?.();
          if (body?.error) errMsg = body?.error;
        } catch {}
        throw new Error(errMsg);
      }
      if (data?.error) throw new Error(data.error);
      setClientSecret(data?.clientSecret);
      setStep('payment');
    } catch (err) {
      setPaymentError(err?.message || 'Failed to initialize payment. Please try again.');
    } finally {
      setLoadingPayment(false);
    }
  };

  const handleActivated = () => {
    setActivationDate(new Date());
    setStep('success');
    // onActivated is called later when user clicks "Access the App" or "Manage Billing"
  };

  // Success screen helpers
  const startDate = activationDate || new Date();
  const renewalDate = new Date(startDate);
  renewalDate?.setMonth(renewalDate?.getMonth() + 1);
  const formatDate = (d) => d?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        {step !== 'success' && (
          <div className="p-6 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <Icon name="Clock" size={24} color="#dc2626" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-heading font-bold text-foreground">Free Trial Expired</h2>
                <p className="text-sm text-muted-foreground">Select a plan to continue using SERVICEJOCKEY</p>
              </div>
              <button
                onClick={signOut}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-colors"
                title="Log out"
              >
                <Icon name="LogOut" size={15} color="currentColor" />
                <span>Log Out</span>
              </button>
            </div>
          </div>
        )}

        <div className="p-6">
          {/* Step Indicator */}
          {step !== 'success' && (
            <div className="flex items-center gap-2 mb-6">
              {['plan', 'billing', 'payment']?.map((s, i) => (
                <React.Fragment key={s}>
                  <div className={`flex items-center gap-1.5 text-sm font-medium ${
                    step === s ? 'text-primary' : i < ['plan','billing','payment']?.indexOf(step) ? 'text-green-600' : 'text-muted-foreground'
                  }`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      step === s ? 'bg-primary text-white' :
                      i < ['plan','billing','payment']?.indexOf(step) ? 'bg-green-600 text-white' : 'bg-muted text-muted-foreground'
                    }`}>
                      {i < ['plan','billing','payment']?.indexOf(step) ? <Icon name="Check" size={12} color="currentColor" /> : i + 1}
                    </div>
                    <span className="hidden sm:inline capitalize">{s}</span>
                  </div>
                  {i < 2 && <div className="flex-1 h-px bg-border" />}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Success Screen */}
          {step === 'success' && (
            <div className="flex flex-col items-center text-center py-4">
              {/* Success Icon */}
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-5">
                <div className="w-14 h-14 bg-green-500 rounded-full flex items-center justify-center">
                  <Icon name="Check" size={32} color="white" />
                </div>
              </div>

              <h2 className="text-2xl font-heading font-bold text-foreground mb-2">Subscription Activated!</h2>
              <p className="text-muted-foreground text-sm mb-8 max-w-sm">
                Welcome to SERVICEJOCKEY {selectedPlanData?.name}. Your account is now fully active and ready to use.
              </p>

              {/* Plan Details Card */}
              <div className="w-full bg-primary/5 border border-primary/20 rounded-2xl p-5 mb-6 text-left">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="CreditCard" size={16} color="var(--color-primary, #2563eb)" />
                  </div>
                  <h3 className="font-semibold text-foreground text-sm">Subscription Details</h3>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Plan</p>
                    <p className="text-sm font-semibold text-foreground capitalize">{selectedPlanData?.name}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Users</p>
                    <p className="text-sm font-semibold text-foreground">{userCount} {userCount === 1 ? 'user' : 'users'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Monthly Total</p>
                    <div className="flex items-baseline gap-1.5">
                      <p className="text-sm font-bold text-primary">${monthlyTotal}/mo</p>
                      {appliedPromo && (
                        <span className="text-xs line-through text-muted-foreground">${baseMonthlyTotal}</span>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Billing</p>
                    <p className="text-sm font-semibold text-foreground">Monthly</p>
                  </div>
                </div>

                <div className="border-t border-primary/20 mt-4 pt-4 grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium flex items-center gap-1">
                      <Icon name="Calendar" size={11} color="currentColor" />
                      Start Date
                    </p>
                    <p className="text-sm font-semibold text-foreground">{formatDate(startDate)}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium flex items-center gap-1">
                      <Icon name="RefreshCw" size={11} color="currentColor" />
                      Next Renewal
                    </p>
                    <p className="text-sm font-semibold text-foreground">{formatDate(renewalDate)}</p>
                  </div>
                </div>

                {appliedPromo && (
                  <div className="border-t border-primary/20 mt-4 pt-4">
                    <div className="flex items-center gap-2">
                      <Icon name="Tag" size={14} color="#16a34a" />
                      <span className="text-sm text-green-700">
                        Promo <span className="font-semibold">{appliedPromo?.code}</span> applied — {formatDiscount(appliedPromo)}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Confirmation email note */}
              {billingForm?.billing_email && (
                <div className="w-full flex items-center gap-2 bg-muted rounded-xl p-3 mb-6">
                  <Icon name="Mail" size={15} color="currentColor" className="text-muted-foreground flex-shrink-0" />
                  <p className="text-xs text-muted-foreground text-left">
                    A confirmation receipt will be sent to <span className="font-medium text-foreground">{billingForm?.billing_email}</span>
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={() => {
                    onActivated?.();
                  }}
                  className="flex items-center justify-center gap-2 py-3 bg-primary text-primary-foreground rounded-xl font-semibold text-sm hover:opacity-90 transition-opacity"
                >
                  <Icon name="LayoutDashboard" size={16} color="currentColor" />
                  Access the App
                </button>
                <button
                  onClick={async () => {
                    try {
                      const { data } = await supabase?.functions?.invoke('create-customer-portal-session', {
                        body: { returnUrl: window?.location?.href }
                      });
                      if (data?.url) window.open(data?.url, '_blank');
                    } catch {
                      // fallback: just close and go to billing settings
                    }
                    onActivated?.();
                  }}
                  className="flex items-center justify-center gap-2 py-3 border border-border text-foreground rounded-xl font-medium text-sm hover:bg-muted transition-colors"
                >
                  <Icon name="Settings" size={16} color="currentColor" />
                  Manage Billing
                </button>
              </div>
            </div>
          )}

          {/* Step 1: Plan Selection */}
          {step === 'plan' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {PLANS?.map(plan => (
                  <div
                    key={plan?.id}
                    onClick={() => { setSelectedPlan(plan?.id); setUserCount(Math.min(userCount, plan?.maxUsers === 999 ? userCount : plan?.maxUsers)); }}
                    className={`relative rounded-xl border-2 p-4 cursor-pointer transition-all ${
                      selectedPlan === plan?.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                    }`}
                  >
                    {plan?.badge && (
                      <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-primary text-primary-foreground rounded-full text-xs font-semibold">
                        {plan?.badge}
                      </span>
                    )}
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold text-foreground text-sm">{plan?.name}</h3>
                      {selectedPlan === plan?.id && (
                        <div className="w-4 h-4 bg-primary rounded-full flex items-center justify-center">
                          <Icon name="Check" size={10} color="white" />
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <span className="text-2xl font-bold text-foreground">${plan?.pricePerUser}</span>
                      <span className="text-xs text-muted-foreground">/user/mo</span>
                    </div>
                    <ul className="space-y-1">
                      {plan?.features?.slice(0, 3)?.map((f, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                          <Icon name="Check" size={12} color="#16a34a" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="bg-muted rounded-xl p-4">
                <label className="text-sm font-medium text-foreground block mb-2">
                  Number of users: <span className="font-bold text-primary">{userCount}</span>
                </label>
                <input
                  type="range"
                  min={1}
                  max={selectedPlanData?.maxUsers === 999 ? 100 : selectedPlanData?.maxUsers}
                  value={userCount}
                  onChange={(e) => setUserCount(parseInt(e?.target?.value))}
                  className="w-full accent-primary"
                />
                <div className="flex justify-between mt-2">
                  <span className="text-xs text-muted-foreground">Monthly Total:</span>
                  <span className="text-sm font-bold text-primary">${baseMonthlyTotal}/mo</span>
                </div>
              </div>

              <button
                onClick={() => setStep('billing')}
                className="w-full py-3 bg-primary text-primary-foreground rounded-lg font-semibold text-sm hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              >
                Continue to Billing <Icon name="ArrowRight" size={16} color="currentColor" />
              </button>
            </div>
          )}

          {/* Step 2: Billing Info */}
          {step === 'billing' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Billing Name / Company *</label>
                <input
                  type="text"
                  value={billingForm?.billing_name}
                  onChange={(e) => setBillingForm(prev => ({ ...prev, billing_name: e?.target?.value }))}
                  placeholder="Company LLC"
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Billing Email *</label>
                <input
                  type="email"
                  value={billingForm?.billing_email}
                  onChange={(e) => setBillingForm(prev => ({ ...prev, billing_email: e?.target?.value }))}
                  placeholder="billing@company.com"
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Billing Address</label>
                <textarea
                  value={billingForm?.billing_address}
                  onChange={(e) => setBillingForm(prev => ({ ...prev, billing_address: e?.target?.value }))}
                  placeholder="1234 Main St, Houston, TX 77001"
                  rows={2}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                />
              </div>

              {/* Promo Code Section */}
              <div>
                {appliedPromo ? (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center">
                          <Icon name="Tag" size={14} color="#16a34a" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-green-800 text-sm">{appliedPromo?.code}</span>
                            <span className="px-2 py-0.5 bg-green-200 text-green-800 text-xs rounded-full font-medium">
                              {formatDiscount(appliedPromo)}
                            </span>
                          </div>
                          <p className="text-xs text-green-600 mt-0.5">Promo code applied successfully</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleRemovePromo}
                        className="text-xs text-green-700 hover:text-red-600 underline transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : showPromoField ? (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <input
                          type="text"
                          value={promoCodeInput}
                          onChange={(e) => { setPromoCodeInput(e?.target?.value?.toUpperCase()); setPromoError(''); }}
                          onKeyDown={(e) => e?.key === 'Enter' && handleValidatePromo()}
                          placeholder="Enter promo code"
                          autoFocus
                          className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 uppercase placeholder:normal-case"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleValidatePromo}
                        disabled={promoLoading || !promoCodeInput?.trim()}
                        className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
                      >
                        {promoLoading ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : 'Apply'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowPromoField(false); setPromoCodeInput(''); setPromoError(''); }}
                        className="px-3 py-2 border border-border rounded-lg text-sm text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <Icon name="X" size={14} color="currentColor" />
                      </button>
                    </div>
                    {promoError && (
                      <p className="text-xs text-red-600 flex items-center gap-1.5">
                        <Icon name="AlertCircle" size={12} color="currentColor" />
                        {promoError}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setShowPromoField(true)}
                      className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
                    >
                      Have a promotional code?
                    </button>
                  </div>
                )}
              </div>

              <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
                <p className="text-sm font-semibold text-foreground mb-2">Order Summary</p>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground capitalize">{selectedPlan} Plan × {userCount} users</span>
                  <span className={appliedPromo ? 'line-through text-muted-foreground text-xs self-center' : 'font-bold text-primary'}>
                    ${baseMonthlyTotal}/mo
                  </span>
                </div>
                {appliedPromo && (
                  <>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-green-700 flex items-center gap-1">
                        <Icon name="Tag" size={12} color="#15803d" />
                        Promo: {appliedPromo?.code} ({formatDiscount(appliedPromo)})
                      </span>
                      <span className="text-green-700 font-medium">
                        −{appliedPromo?.percentOff
                          ? `$${(baseTotal * appliedPromo?.percentOff / 100)?.toFixed(2)}`
                          : `$${(appliedPromo?.amountOff / 100)?.toFixed(2)}`}
                      </span>
                    </div>
                    <div className="border-t border-primary/20 pt-2 mt-1 flex justify-between text-sm">
                      <span className="font-semibold text-foreground">Total</span>
                      <span className="font-bold text-primary">${monthlyTotal}/mo</span>
                    </div>
                  </>
                )}
              </div>

              {paymentError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{paymentError}</p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => setStep('plan')}
                  className="flex-1 py-3 border border-border text-foreground rounded-lg font-medium text-sm hover:bg-muted transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleProceedToPayment}
                  disabled={loadingPayment}
                  className="flex-1 py-3 bg-primary text-primary-foreground rounded-lg font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {loadingPayment ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Loading...</>
                  ) : (
                    <>Continue to Payment <Icon name="ArrowRight" size={16} color="currentColor" /></>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Payment */}
          {step === 'payment' && clientSecret && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-2">
                <Icon name="Shield" size={16} color="#16a34a" />
                <p className="text-sm text-green-700">Your payment is secured with 256-bit SSL encryption</p>
              </div>
              {appliedPromo && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-2">
                  <Icon name="Tag" size={16} color="#16a34a" />
                  <p className="text-sm text-green-700">
                    Promo code <span className="font-semibold">{appliedPromo?.code}</span> applied — {formatDiscount(appliedPromo)}
                  </p>
                </div>
              )}
              <Elements stripe={stripePromise} options={{ clientSecret, paymentMethodCreation: 'manual' }}>
                <PaymentForm
                  onSuccess={handleActivated}
                  onError={(err) => setPaymentError(err?.message)}
                  planId={selectedPlan}
                  userCount={userCount}
                  billingInfo={billingForm}
                />
              </Elements>
              <button
                onClick={() => setStep('billing')}
                className="w-full py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                ← Back to billing info
              </button>
            </div>
          )}

          {/* No Stripe key fallback */}
          {step === 'payment' && !clientSecret && !loadingPayment && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-sm text-amber-700 font-medium mb-1">Payment Setup Required</p>
                <p className="text-sm text-amber-600">Stripe payment processing is not yet configured. Please contact support to activate your subscription.</p>
              </div>
              <button
                onClick={async () => {
                  // Activate without payment for demo/setup purposes
                  await supabase?.from('org_subscriptions')?.update({
                    status: 'active',
                    plan: selectedPlan,
                    max_users: userCount,
                    payment_method_added: true,
                    account_suspended_at: null,
                    suspension_reason: null,
                    ...billingForm,
                    updated_at: new Date()?.toISOString()
                  })?.not('id', 'is', null);
                  handleActivated();
                }}
                className="w-full py-3 bg-primary text-primary-foreground rounded-lg font-semibold text-sm hover:opacity-90 transition-opacity"
              >
                Activate Account (Demo Mode)
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrialExpiredModal;
